import React, { useState, useCallback } from 'react';
import { fetchWithAuth } from '../services/api';
import DataTable from './DataTable';
import { AlertTriangle } from 'lucide-react';

const OccupancyCell = ({ value }) => value == null ? null : <span>{value.toFixed(1)}%</span>;

export const portfolioTotals = (rows) => {
  const units = rows.reduce((sum, row) => sum + (row['Occupancy Units'] || 0), 0);
  const occupied = rows.reduce((sum, row) => sum + (row['Occupied Units'] || 0), 0);
  // A property with no positive basis has no defined yield and cannot contribute
  // its net cash flow to the yield denominator of other properties.
  const withBasis = rows.filter(row => row['Property Basis'] > 0);
  const basis = withBasis.reduce((sum, row) => sum + row['Property Basis'], 0);
  const yieldFor = (field) => basis ? withBasis.reduce((sum, row) => sum + (row[field] || 0), 0) / basis : null;
  return {
    Occupancy: units ? occupied / units * 100 : null,
    'PHA YTC': yieldFor('PHA Net Cash Flow'),
    'Contract YTC': yieldFor('Contract Net Cash Flow'),
  };
};

const totalFormatters = {
  Occupancy: value => value == null ? '—' : `${value.toFixed(1)}%`,
  'PHA YTC': value => value == null ? '—' : `${(value * 100).toFixed(2)}%`,
  'Contract YTC': value => value == null ? '—' : `${(value * 100).toFixed(2)}%`,
};

// Add this custom cell renderer component
const ViolationCell = ({ value }) => {
  if (!value) return null;
  return <AlertTriangle className="h-5 w-5 text-red-500 mx-auto" />;
};

// Add new custom cell renderer for license expiration date
const LicenseExpirationCell = ({ value }) => {
  if (!value) return null;
  return <span className="text-gray-700">{value}</span>;
};

// Add new custom cell renderer for percentage values
const PercentageCell = ({ value }) => {
  if (value === null || value === undefined) return null;
  return <span className="text-gray-700">{(value * 100).toFixed(2)}%</span>;
};

// Update this custom cell renderer for high water charges to show the amount
const HighWaterCell = ({ value }) => {
  if (!value) return null;
  return <span className="text-blue-500 font-medium">${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>;
};

const CurrencyCell = ({ value }) => {
  if (value === null || value === undefined) return null;
  return (
    <span className="text-gray-700">
      ${Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
    </span>
  );
};

const PortfolioTable = ({ onPropertySelect }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAddresses, setSelectedAddresses] = useState([]);

  const processData = useCallback((rawData) => {
    if (!Array.isArray(rawData)) return [];
    
    return rawData
      .filter(row => row && typeof row === 'object' && Object.keys(row).length > 0)
      .map(row => {
        const processedRow = {};
        Object.entries(row).forEach(([key, value]) => {
          if (typeof value === 'string' && !isNaN(value.replace(/,/g, ''))) {
            processedRow[key] = parseFloat(value.replace(/,/g, ''));
          } else if (typeof value === 'number' && isNaN(value)) {
            processedRow[key] = null;
          } else if (value === 'NaN') {
            processedRow[key] = null;
          } else {
            processedRow[key] = value;
          }
        });

        // Calculate YTC ratios
        if (processedRow['Property Basis'] && processedRow['Property Basis'] > 0) {
          processedRow['PHA YTC'] = processedRow['PHA Net Cash Flow'] / processedRow['Property Basis'];
          processedRow['Contract YTC'] = processedRow['Contract Net Cash Flow'] / processedRow['Property Basis'];
        } else {
          processedRow['PHA YTC'] = null;
          processedRow['Contract YTC'] = null;
        }

        return processedRow;
      });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth('/api/data/portfolio');
      const result = await response.json();
      
      const processedData = processData(result || []);
      setData(processedData);
    } catch (err) {
      console.error('Fetch data error:', err);
    } finally {
      setLoading(false);
    }
  }, [processData]);

  const handleRowSelect = (selectedIds) => {
    setSelectedAddresses(selectedIds);
  };

  const handlePropertySearch = () => {
    if (selectedAddresses.length === 1) {
      const selectedProperty = data.find(row => row.Property === selectedAddresses[0]);
      if (selectedProperty) {
        onPropertySelect(selectedProperty.Property);
      }
    }
  };

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    'Property',
    'Owner',
    'Units',
    'Occupancy',
    'TTM Net Cash Flow',
    'PHA YTC',
    'Contract YTC',
    'Property Basis',
    'Active Violations',
    'No Active License',
    'License Expiration',
    'High Water Charges',
    'This Year Property Taxes',
    'Last Year Property Taxes',
    'Property Taxes Outstanding',
    'Trash Fees Outstanding',
    'Total Taxes & Fees Outstanding'
  ];

  return (
    <div className="flex flex-col h-full w-full">
      <div className="bg-white rounded-lg shadow p-4 flex-grow flex flex-col">
        {selectedAddresses.length === 1 && onPropertySelect && (
          <div className="mb-4">
            <button
              onClick={handlePropertySearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Property Search
            </button>
          </div>
        )}
        
        {loading ? (
          <div className="text-gray-500">Loading...</div>
        ) : data && data.length > 0 ? (
          <div className="flex-grow">
            <DataTable 
              data={data}
              visibleColumns={columns}
              columnOrder={columns}
              initialSort={[
                {
                  id: 'Active Violations',
                  desc: false
                }
              ]}
              primaryKey="Property"
              onRowSelect={handleRowSelect}
              aggregateTotals={portfolioTotals}
              totalFormatters={totalFormatters}
              customCells={{
                'Occupancy': OccupancyCell,
                'Active Violations': ViolationCell,
                'No Active License': ViolationCell,
                'License Expiration': LicenseExpirationCell,
                'High Water Charges': HighWaterCell,
                'This Year Property Taxes': CurrencyCell,
                'Last Year Property Taxes': CurrencyCell,
                'PHA YTC': PercentageCell,
                'Contract YTC': PercentageCell
              }}
              columnLabels={{}}
            />
          </div>
        ) : (
          <div className="text-gray-500">No portfolio data available</div>
        )}
      </div>
    </div>
  );
};

export default PortfolioTable;
